import { useState, useRef, useEffect } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import { MdCloudUpload } from "react-icons/md";
import { BiSolidFileBlank, BiImage, BiSolidFilePdf } from "react-icons/bi";
import SpinWheelService from "../../Services/SpinWheelService";
import { toast } from "react-toastify";
import PropertyImageModel from "./PropertyImageModel";
import PropertiesService from "../../Services/PropertiesService";
import { showErrorToast } from "../../Constant/Notification";

const MediaUpload = ({
  mediaType,
  files,
  onChange,
  maxFiles = 5,
  accept,
  folderpath,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [fileTypes, setFileTypes] = useState({});
  const [previews, setPreviews] = useState({});
  const [labels, setLabels] = useState({});

  const getFileType = async (url) => {
    if (fileTypes[url]) {
      return fileTypes[url];
    }
    try {
      const body = { url };
      const response = await PropertiesService.getDocumentType(body);
      const contentType = response?.data?.type || "unknown";
      setFileTypes((prev) => ({ ...prev, [url]: contentType }));
      return contentType;
    } catch (error) {
      console.error(`Error fetching file type for ${url}:`, error);
      return "unknown";
    }
  };


  const handleImageUpload = async (file) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await SpinWheelService.createBannerImageTemplate(
        formData,
        folderpath
      );

      if (response?.data?.imageUrl) {
        const imageUrl = response.data.imageUrl;

        const body = { url: imageUrl };
        const typeResponse = await PropertiesService.getDocumentType(body);
        const contentType = typeResponse?.data?.type;

        if (!contentType) {
          throw new Error("Failed to retrieve file type from server");
        }

        const allowedTypes = accept.split(",").map((type) => type.trim());
        const isValidType = allowedTypes.some(
          (type) =>
            type === contentType ||
            (type.endsWith("/*") &&
              contentType.startsWith(type.replace("/*", "")))
        );

        if (!isValidType) {
          throw new Error(`Uploaded file type '${contentType}' is not allowed`);
        }

        if (
          mediaType === "propertyDocuments" &&
          contentType.startsWith("image/")
        ) {
          throw new Error("Image files are not allowed for property documents");
        }

      
        setFileTypes((prev) => ({ ...prev, [imageUrl]: contentType }));
        return imageUrl; 
      }

      throw new Error("Failed to get file URL from the response");
    } catch (error) {
      showErrorToast("File upload failed: " + error.message);
      console.error("File upload error:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length > 0) {
      if (files.length + newFiles.length > maxFiles) {
        showErrorToast(
          `You can only upload a maximum of ${maxFiles} ${mediaType}`
        );
        return;
      }

      const validFiles = newFiles.filter((file) => {
        const allowedTypes = accept.split(",");
        return allowedTypes.some(
          (type) =>
            type === file.type ||
            (type.endsWith("/*") &&
              file.type.startsWith(type.replace("/*", "")))
        );
      });

      if (validFiles.length === 0) {
        showErrorToast(`Please upload valid ${mediaType} files`);
        return;
      }

      const uploadedUrls = [];
      for (const file of validFiles) {
        const maxSizeMB = 1;
        if (file.size > maxSizeMB * 1024 * 1024) {
          showErrorToast("File must be less than 1MB.");
          return;
        }
        console.log(
          `Uploading: ${file.name}, Size: ${(file.size / 1024 / 1024).toFixed(
            2
          )} MB`
        );

        const fileUrl = await handleImageUpload(file);
        if (fileUrl) {
          uploadedUrls.push(fileUrl);
        }
      }

      if (uploadedUrls.length > 0) {
        onChange([...files, ...uploadedUrls]);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle drag-and-drop
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.filter((file) => {
        const allowedTypes = accept.split(",");
        return allowedTypes.some(
          (type) =>
            type === file.type ||
            (type.endsWith("/*") &&
              file.type.startsWith(type.replace("/*", "")))
        );
      });

      if (validFiles.length === 0) {
        showErrorToast(`Please drop valid ${mediaType} files`);
        return;
      }

      if (files.length + validFiles.length > maxFiles) {
        showErrorToast(
          `You can only upload a maximum of ${maxFiles} ${mediaType}`
        );
        return;
      }

      const uploadedUrls = [];
      for (const file of validFiles) {
        const maxSizeMB = 1;
        if (file.size > maxSizeMB * 1024 * 1024) {
          showErrorToast("File must be less than 1MB.");
          break;
        }

        const fileUrl = await handleImageUpload(file);
        if (fileUrl) {
          uploadedUrls.push(fileUrl);
        }
      }

      if (uploadedUrls.length > 0) {
        onChange([...files, ...uploadedUrls]);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragActive) setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleRemove = (index, e) => {
    e.stopPropagation();
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(updatedFiles);
  };

  const isLimitReached = files.length >= maxFiles;

  // Determine file type for preview and click handling
  const isImageFile = async (fileUrl) => {
    const contentType = await getFileType(fileUrl);
    return contentType.startsWith("image/");
  };

  const isPdfFile = async (fileUrl) => {
    const contentType = await getFileType(fileUrl);
    return contentType === "application/pdf";
  };

  const isDwgFile = async (fileUrl) => {
    const contentType = await getFileType(fileUrl);
    return (
      contentType === "application/dwg" || contentType === "application/x-dwg"
    );
  };

  const isDxfFile = async (fileUrl) => {
    const contentType = await getFileType(fileUrl);
    return (
      contentType === "application/dxf" || contentType === "application/x-dxf"
    );
  };

  // Generate media type label
  const getMediaTypeLabel = (contentType, fileUrl) => {
    if (contentType.startsWith("image/")) {
      return {
        icon: <BiImage className="text-blue-500" />,
        label: `{ "type": "${contentType}" }`,
        color: "bg-blue-100 text-blue-700",
      };
    } else if (contentType === "application/pdf") {
      return {
        icon: <BiSolidFilePdf className="text-red-500" />,
        label: `{ "type": "${contentType}" }`,
        color: "bg-red-100 text-red-700",
      };
    } else if (
      contentType === "application/dwg" ||
      contentType === "application/x-dwg"
    ) {
      return {
        icon: <BiSolidFileBlank className="text-purple-500" />,
        label: `{ "type": "${contentType}" }`,
        color: "bg-purple-100 text-purple-700",
      };
    } else if (
      contentType === "application/dxf" ||
      contentType === "application/x-dxf"
    ) {
      return {
        icon: <BiSolidFileBlank className="text-green-500" />,
        label: `{ "type": "${contentType}" }`,
        color: "bg-green-100 text-green-700",
      };
    } else {
      const ext = fileUrl.split(".").pop()?.toUpperCase() || "File";
      return {
        icon: <BiSolidFileBlank className="text-gray-500" />,
        label: `{ "type": "${ext.toLowerCase()}" }`,
        color: "bg-gray-100 text-gray-700",
      };
    }
  };

  // Generate preview content
  const getPreviewContent = (contentType, fileUrl) => {
    try {
      if (contentType.startsWith("image/")) {
        return (
          <img
            src={fileUrl}
            className="w-full h-full object-cover rounded"
            alt="Preview"
          />
        );
      } else if (contentType === "application/pdf") {
        return (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <BiSolidFilePdf className="text-3xl text-red-500" />
            <span className="text-sm text-gray-600 ml-2">PDF</span>
          </div>
        );
      } else if (
        contentType === "application/dwg" ||
        contentType === "application/x-dwg"
      ) {
        return (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <BiSolidFileBlank className="text-3xl text-purple-500" />
            <span className="text-sm text-gray-600 ml-2">DWG</span>
          </div>
        );
      } else if (
        contentType === "application/dxf" ||
        contentType === "application/x-dxf"
      ) {
        return (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <BiSolidFileBlank className="text-3xl text-green-500" />
            <span className="text-sm text-gray-600 ml-2">DXF</span>
          </div>
        );
      } else {
        return (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <BiSolidFileBlank className="text-3xl text-gray-500" />
            <span className="text-sm text-gray-600 ml-2">Document</span>
          </div>
        );
      }
    } catch (error) {
      console.error("Error generating preview:", error);
      return (
        <div className="flex items-center justify-center h-full bg-gray-50 text-red-600">
          Error loading preview
        </div>
      );
    }
  };

  // Pre-fetch file types and generate previews/labels
  useEffect(() => {
    const fetchFileTypes = async () => {
      const newPreviews = {};
      const newLabels = {};
      const validFiles = [];
      for (const fileUrl of files) {
        const contentType = await getFileType(fileUrl);
        // Reject images for propertyDocuments
        if (
          mediaType === "propertyDocuments" &&
          contentType.startsWith("image/")
        ) {
          showErrorToast("Image files are not allowed for property documents");
          continue;
        }
        validFiles.push(fileUrl);
        newPreviews[fileUrl] = getPreviewContent(contentType, fileUrl);
        newLabels[fileUrl] = getMediaTypeLabel(contentType, fileUrl);
      }
      setPreviews(newPreviews);
      setLabels(newLabels);
      // Update files to exclude invalid types
      if (validFiles.length < files.length) {
        onChange(validFiles);
      }
    };
    fetchFileTypes();
  }, [files, mediaType, onChange]);

  const getFileName = (fileUrl) => {
    if (!fileUrl) return "Unnamed file";
    try {
      const urlObj = new URL(fileUrl);
      return decodeURIComponent(urlObj.pathname.split("/").pop()) || "File";
    } catch {
      return fileUrl.split("/").pop() || "File";
    }
  };

  const handleFileClick = async (fileUrl, e) => {
    e.stopPropagation();
    const contentType = await getFileType(fileUrl);
    if (
      mediaType === "propertyBlueprints" &&
      contentType.startsWith("image/")
    ) {
      setSelectedImage(fileUrl);
      setIsOpen(true);
    } else {
      window.open(fileUrl, "_blank");
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-700">
          {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} (
          {files.length}/{maxFiles})
        </h3>
      </div>

      <div
        className={`
          border rounded-lg mb-4 transition-all
          ${
            dragActive
              ? "border-emerald-500 bg-emerald-50"
              : "border-dashed border-gray-300"
          }
          ${
            isLimitReached || loading
              ? "opacity-60 cursor-not-allowed"
              : "cursor-pointer hover:border-emerald-300"
          }
          ${files.length === 0 ? "p-4" : "p-2"}
        `}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={(e) => {
          if (!isLimitReached && !loading && e.target === e.currentTarget) {
            fileInputRef.current?.click();
          }
        }}
      >
        {files.length === 0 ? (
          <div
            onClick={() => !isLimitReached && fileInputRef.current?.click()}
            className="flex flex-col items-center justify-center py-6"
          >
            <MdCloudUpload className="text-3xl text-emerald-500 mb-2" />
            <div className="text-sm text-gray-600 flex items-center gap-2 flex-col">
              <div>
                {dragActive
                  ? `Drop ${mediaType} here...`
                  : `Drag & drop ${mediaType} here or click to upload`}
              </div>
              <div className="text-customGreen">
                {loading ? "Uploading..." : ""}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {files.map((fileUrl, index) => (
              <div
                key={index}
                className="relative h-24 border rounded overflow-hidden bg-white group"
                onClick={(e) => handleFileClick(fileUrl, e)}
              >
                {previews[fileUrl] || (
                  <div className="flex items-center justify-center h-full bg-gray-50">
                    <span className="text-sm text-gray-600">Loading...</span>
                  </div>
                )}
                {labels[fileUrl] && (
                  <div className="absolute top-1 left-1">
                    <div
                      className={`text-xs flex items-center gap-2 px-1.5 py-0.5 rounded-sm ${labels[fileUrl].color}`}
                    >
                      <div> {labels[fileUrl].icon} </div>
                     
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1">
                  <p className="text-white text-xs truncate w-full">
                    {getFileName(fileUrl)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleRemove(index, e)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <IoCloseCircleOutline className="text-red-500 text-lg" />
                </button>
              </div>
            ))}
            {!isLimitReached && (
              <div
                className="h-24 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => !loading && fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center text-gray-400">
                  <MdCloudUpload className="text-xl" />
                  <span className="text-xs mt-1">Add more</span>
                </div>
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleUpload}
          className="hidden"
          disabled={isLimitReached || loading}
          accept={accept}
        />
      </div>
      <PropertyImageModel
        isOpen={isOpen}
        isClose={() => {
          setIsOpen(false);
          setSelectedImage(null);
        }}
        selectedImage={selectedImage}
      />
    </div>
  );
};

export default MediaUpload;
